import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";

interface CandlestickLoaderProps {
  isLoading: boolean;
  messages?: string[];
  onComplete?: () => void;
  estimatedDuration?: number;
  width?: number;
  height?: number;
  containerClassName?: string;
  messageClassName?: string;
  context?: string;
  panelClassName?: string;
}

interface CandleData {
  open: number;
  high: number;
  low: number;
  close: number;
}

interface NormalizedCandle extends CandleData {
  color: "green" | "red";
}

interface CandlePositions {
  bodyBottom: number;
  bodyHeight: number;
  upperWickTop: number;
  upperWickHeight: number;
  lowerWickBottom: number;
  lowerWickHeight: number;
  color: "green" | "red";
}

const DEFAULT_MESSAGES = [
  "Preparing data",
  "Analyzing trends",
  "Optimizing parameters",
  "Balancing strategies",
];

const HISTORICAL_DATA: CandleData[] = [
  { open: 42000, high: 42500, low: 41800, close: 42300 },
  { open: 42300, high: 42800, low: 42100, close: 42750 },
  { open: 42750, high: 43200, low: 42600, close: 43100 },
  { open: 43100, high: 43150, low: 42500, close: 42600 },
  { open: 42600, high: 42900, low: 42400, close: 42850 },
  { open: 42850, high: 43500, low: 42850, close: 43400 },
  { open: 43400, high: 43600, low: 43350, close: 43500 },
  { open: 43500, high: 43900, low: 43200, close: 43300 },
  { open: 43300, high: 43400, low: 42800, close: 42900 },
  { open: 42900, high: 43100, low: 42700, close: 43000 },
  { open: 43000, high: 44200, low: 43000, close: 44100 },
  { open: 44100, high: 44500, low: 44000, close: 44450 },
  { open: 44450, high: 44600, low: 43900, close: 44000 },
  { open: 44000, high: 44200, low: 43600, close: 43700 },
  { open: 43700, high: 43800, low: 43100, close: 43200 },
  { open: 43200, high: 43400, low: 43000, close: 43350 },
  { open: 43350, high: 43900, low: 43300, close: 43850 },
  { open: 43850, high: 44300, low: 43800, close: 44200 },
  { open: 44200, high: 44400, low: 44100, close: 44150 },
  { open: 44150, high: 44300, low: 43800, close: 43900 },
  { open: 43900, high: 44100, low: 43700, close: 44050 },
  { open: 44050, high: 44800, low: 44050, close: 44750 },
  { open: 44750, high: 45200, low: 44700, close: 45100 },
  { open: 45100, high: 45300, low: 44600, close: 44700 },
  { open: 44700, high: 44900, low: 44500, close: 44850 },
  { open: 44850, high: 45000, low: 44200, close: 44300 },
  { open: 44300, high: 44400, low: 43900, close: 44000 },
  { open: 44000, high: 44200, low: 43800, close: 44150 },
  { open: 44150, high: 44600, low: 44100, close: 44550 },
  { open: 44550, high: 45100, low: 44500, close: 45000 },
  { open: 45000, high: 45800, low: 44900, close: 45700 },
  { open: 45700, high: 45750, low: 44800, close: 44850 },
  { open: 44850, high: 45000, low: 44700, close: 44900 },
  { open: 44900, high: 44950, low: 44850, close: 44875 },
  { open: 44875, high: 45200, low: 44875, close: 45150 },
  { open: 45150, high: 45150, low: 44600, close: 44650 },
];

const WINDOW_SIZE = 4;
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 250;

const normalizeData = (window: CandleData[]): NormalizedCandle[] => {
  let min = Infinity;
  let max = -Infinity;

  window.forEach((candle) => {
    min = Math.min(min, candle.low);
    max = Math.max(max, candle.high);
  });

  const range = max - min;
  const padding = range * 0.1;
  const paddedMin = min - padding;
  const paddedMax = max + padding;

  return window.map((candle) => ({
    open: ((candle.open - paddedMin) / (paddedMax - paddedMin)) * 100,
    high: ((candle.high - paddedMin) / (paddedMax - paddedMin)) * 100,
    low: ((candle.low - paddedMin) / (paddedMax - paddedMin)) * 100,
    close: ((candle.close - paddedMin) / (paddedMax - paddedMin)) * 100,
    color: candle.close >= candle.open ? "green" : "red",
  }));
};

const getCandlePositions = (normalizedCandle: NormalizedCandle): CandlePositions => {
  const { open, high, low, close, color } = normalizedCandle;

  const bodyBottom = Math.min(open, close);
  const bodyTop = Math.max(open, close);
  let bodyHeight = bodyTop - bodyBottom;

  const minBodyHeight = 2;
  const minWickHeight = 2;

  let finalBodyBottom = bodyBottom;
  let finalBodyTop = bodyTop;

  if (bodyHeight < minBodyHeight) {
    const bodyCenter = (bodyBottom + bodyTop) / 2;
    finalBodyBottom = bodyCenter - minBodyHeight / 2;
    finalBodyTop = bodyCenter + minBodyHeight / 2;
    bodyHeight = minBodyHeight;
  }

  let upperWickHeight = high - finalBodyTop;
  let lowerWickHeight = finalBodyBottom - low;

  if (upperWickHeight > 0) {
    upperWickHeight = Math.max(minWickHeight, upperWickHeight);
  }
  if (lowerWickHeight > 0) {
    lowerWickHeight = Math.max(minWickHeight, lowerWickHeight);
  }

  return {
    bodyBottom: finalBodyBottom,
    bodyHeight,
    upperWickTop: 100 - high,
    upperWickHeight: Math.max(0, upperWickHeight),
    lowerWickBottom: low,
    lowerWickHeight: Math.max(0, lowerWickHeight),
    color,
  };
};

const CandlestickLoader: React.FC<CandlestickLoaderProps> = ({
  isLoading,
  messages,
  onComplete,
  estimatedDuration = 15,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  containerClassName,
  messageClassName,
  context = "CandlestickLoader",
  panelClassName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [candlePositions, setCandlePositions] = useState<CandlePositions[]>([]);

  const messagesKey = useMemo(
    () => JSON.stringify(messages ?? []),
    [messages],
  );

  const messageSequence = useMemo(() => {
    const customMessages = (messages ?? [])
      .map((msg) => (typeof msg === "string" ? msg.trim() : ""))
      .filter((msg) => msg.length > 0);

    return customMessages.length > 0 ? customMessages : DEFAULT_MESSAGES;
  }, [messagesKey]);

  useEffect(() => {
    if (!isLoading) {
      setCurrentIndex(0);
      setCurrentMessageIndex(0);
      setCandlePositions([]);
      return;
    }

    logger.info("CandlestickLoader.start", {
      context,
      estimatedDuration,
      messageCount: messageSequence.length,
    });

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % HISTORICAL_DATA.length);
      setCurrentMessageIndex((prev) => {
        if (messageSequence.length === 0) return 0;
        return (prev + 1) % messageSequence.length;
      });
    }, 1500);

    const timeout =
      estimatedDuration && estimatedDuration > 0
        ? setTimeout(() => {
            logger.info("CandlestickLoader.complete", { context });
            onComplete?.();
          }, estimatedDuration * 1000)
        : undefined;

    return () => {
      clearInterval(interval);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    context,
    estimatedDuration,
    isLoading,
    messageSequence.length,
    onComplete,
  ]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    setCurrentMessageIndex(0);
  }, [isLoading, messagesKey]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const window: CandleData[] = [];
    for (let i = 0; i < WINDOW_SIZE; i++) {
      const index = (currentIndex + i) % HISTORICAL_DATA.length;
      const candleData = HISTORICAL_DATA[index];
      if (candleData) {
        window.push(candleData);
      }
    }

    const normalizedData = normalizeData(window);
    const positions = normalizedData.map((candle) => getCandlePositions(candle));
    setCandlePositions(positions);
  }, [currentIndex, isLoading]);

  if (!isLoading) return null;

  const containerClasses = cn(
    "flex items-center justify-center min-h-[400px]",
    containerClassName,
  );

  const chartStyle = {
    width,
    height,
  } as const;

  const panelClasses = cn(
    "relative rounded-2xl border border-white/10 bg-background/80 backdrop-blur-xl shadow-[0_25px_80px_-35px_rgba(15,23,42,0.65)]",
    panelClassName,
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={containerClasses}
      >
        <div className="relative">
          <div className={panelClasses} style={chartStyle}>
            <div className="pointer-events-none absolute inset-0">
              {[20, 40, 60, 80].map((percent) => (
                <div
                  key={percent}
                  className="absolute h-px w-full bg-border/30"
                  style={{ top: `${percent}%` }}
                />
              ))}
            </div>

            <div className="absolute inset-0">
              {candlePositions.map((positions, index) => (
                <div
                  key={`candle-${index}`}
                  className="absolute h-full w-[25px]"
                  style={{ left: `${15 + index * 20}%` }}
                >
                  <div className="pointer-events-none absolute left-[11px] h-full w-[3px]">
                    <div
                      className="absolute w-full transition-all duration-800 ease-out"
                      style={{
                        top: `${positions.upperWickTop}%`,
                        height: `${positions.upperWickHeight}%`,
                        backgroundColor:
                          positions.color === "green" ? "#64DD17" : "#FF1744",
                      }}
                    />
                    <div
                      className="absolute w-full transition-all duration-800 ease-out"
                      style={{
                        bottom: `${positions.lowerWickBottom}%`,
                        height: `${positions.lowerWickHeight}%`,
                        backgroundColor:
                          positions.color === "green" ? "#64DD17" : "#FF1744",
                      }}
                    />
                  </div>

                  <div
                    className="absolute left-0 w-full transition-all duration-800 ease-out"
                    style={{
                      bottom: `${positions.bodyBottom}%`,
                      height: `${positions.bodyHeight}%`,
                    }}
                  >
                    <div
                      className="absolute h-full w-full rounded-sm transition-opacity duration-800 ease-out"
                      style={{
                        background: "linear-gradient(to top, #00C853, #64DD17)",
                        boxShadow:
                          positions.color === "green"
                            ? positions.bodyHeight > 15
                              ? "0 0 20px 8px rgba(0, 200, 83, 0.6)"
                              : "0 0 15px 5px rgba(0, 200, 83, 0.4)"
                            : "0 0 15px 5px rgba(0, 200, 83, 0)",
                        opacity: positions.color === "green" ? 1 : 0,
                      }}
                    />
                    <div
                      className="absolute h-full w-full rounded-sm transition-opacity duration-800 ease-out"
                      style={{
                        background: "linear-gradient(to top, #D50000, #FF1744)",
                        boxShadow:
                          positions.color === "red"
                            ? positions.bodyHeight > 15
                              ? "0 0 15px 5px rgba(213, 0, 0, 0.4)"
                              : "0 0 15px 5px rgba(213, 0, 0, 0.4)"
                            : "0 0 15px 5px rgba(213, 0, 0, 0)",
                        opacity: positions.color === "red" ? 1 : 0,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {messageSequence.length > 0 && (
              <motion.p
                className={cn(
                  "text-sm uppercase tracking-[3px] text-muted-foreground",
                  messageClassName,
                )}
                key={`${context}-${currentMessageIndex}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.7, 1, 0.7] }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                  default: { duration: 0.5 },
                }}
              >
                {messageSequence[currentMessageIndex]}
              </motion.p>
            )}
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CandlestickLoader;
